/**
 * MontaTech v2 - ActionBar Component
 *
 * Barra de acoes: Coletar -> Calcular -> Melhorar -> Aplicar
 */

import { useState } from 'react';
import { Play, RotateCcw, XCircle, Zap, CheckCircle, TrendingUp, ChevronDown } from 'lucide-react';
import type { MontaTechStatus } from '../types';
import type { OptimizeLevel } from '../services/nestingCalculator';
import { OPTIMIZE_ATTEMPTS } from '../services/nestingCalculator';

const OPTIMIZE_LEVELS: { key: OptimizeLevel; label: string; attempts: number }[] = [
  { key: 'light', label: 'Light', attempts: OPTIMIZE_ATTEMPTS.light },
  { key: 'classic', label: 'Classic', attempts: OPTIMIZE_ATTEMPTS.classic },
  { key: 'premium', label: 'Premium', attempts: OPTIMIZE_ATTEMPTS.premium },
];

interface ActionBarProps {
  status: MontaTechStatus;
  canCollect: boolean;
  canCalculate: boolean;
  canOptimize: boolean;
  canApply: boolean;
  canCancel: boolean;
  hasResult: boolean;
  isProcessing: boolean;
  onCollect: () => void;
  onCalculate: () => void;
  onOptimize: (level: OptimizeLevel) => void;
  onApply: () => void;
  onCancel: () => void;
  onReset: () => void;
}

export function ActionBar({
  status,
  canCollect,
  canCalculate,
  canOptimize,
  canApply,
  canCancel,
  hasResult,
  isProcessing,
  onCollect,
  onCalculate,
  onOptimize,
  onApply,
  onCancel,
  onReset
}: ActionBarProps) {
  const [showOptimizeMenu, setShowOptimizeMenu] = useState(false);
  const isCalculating = status === 'calculating';
  const isCollecting = status === 'collecting';
  const isApplying = status === 'applying';
  const isOptimizing = status === 'optimizing';

  if (hasResult) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border-t border-navy-700/80 bg-navy-900/95">
        <div className="relative min-w-0 flex-[1]">
          <button
            onClick={() => setShowOptimizeMenu(!showOptimizeMenu)}
            disabled={!canOptimize || isProcessing}
            className={`flex h-9 w-full items-center justify-center gap-0.5 rounded-md border px-1 text-[11px] font-medium transition-colors ${
              !canOptimize || isProcessing
                ? 'border-slate-400/10 bg-slate-500/5 text-slate-400/30 cursor-not-allowed'
                : 'border-slate-400/20 bg-slate-400/10 text-slate-200 hover:bg-slate-400/15'
            }`}
            title="Otimizar resultado com mais tentativas"
          >
            <TrendingUp className={`h-3.5 w-3.5 ${isOptimizing ? 'animate-pulse' : ''}`} />
            Melhorar
          </button>

          {showOptimizeMenu && !isProcessing && canOptimize && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowOptimizeMenu(false)}
              />
              <div className="absolute bottom-full left-0 z-20 mb-1 min-w-[160px] overflow-hidden rounded-lg border border-navy-600 bg-navy-800 shadow-xl">
                {OPTIMIZE_LEVELS.map(({ key, label, attempts }) => (
                  <button
                    key={key}
                    onClick={() => {
                      setShowOptimizeMenu(false);
                      onOptimize(key);
                    }}
                    className="flex w-full items-center justify-between px-3 py-2 text-xs text-gray-200 transition-colors hover:bg-navy-700"
                  >
                    <span className="font-medium">{label}</span>
                    <span className="text-gray-500">{attempts} tentativas</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <button
          onClick={onApply}
          disabled={!canApply || isProcessing}
          className={`flex h-9 flex-[4] items-center justify-center gap-1.5 rounded-md px-3 text-sm font-semibold transition-colors ${
            !canApply || isProcessing
              ? 'border border-blue-400/10 bg-blue-500/10 text-blue-300/35 cursor-not-allowed'
              : 'bg-blue-600 text-white shadow-sm shadow-blue-950/40 hover:bg-blue-500'
          }`}
          title="Aplicar posicoes nos shapes do CorelDRAW"
        >
          <CheckCircle className={`h-4 w-4 ${isApplying ? 'animate-pulse' : ''}`} />
          {isApplying ? 'Aplicando' : 'Aplicar'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-t border-navy-700/80 bg-navy-900/95">
      {/* Botao Coletar */}
      <button
        onClick={onCollect}
        disabled={!canCollect}
        aria-label="Coletar shapes novamente"
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border text-xs transition-colors ${
          !canCollect
            ? 'border-slate-400/10 bg-slate-500/5 text-slate-400/30 cursor-not-allowed'
            : 'border-slate-400/20 bg-slate-400/10 text-slate-300 hover:border-slate-300/30 hover:bg-slate-400/15 hover:text-slate-100'
        }`}
        title="Coletar shapes do CorelDRAW"
      >
        <Zap className={`w-3.5 h-3.5 ${isCollecting ? 'animate-pulse' : ''}`} />
      </button>

      {/* Botao Calcular OU Cancelar */}
      {canCancel ? (
        <button
          onClick={onCancel}
          className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-red-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-red-700"
        >
          <XCircle className="w-3.5 h-3.5" />
          {isOptimizing ? 'Parar' : 'Cancelar'}
        </button>
      ) : (
        <button
          onClick={onCalculate}
          disabled={!canCalculate}
          className={`flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-semibold transition-colors ${
            !canCalculate
              ? 'border border-blue-400/10 bg-blue-500/10 text-blue-300/35 cursor-not-allowed'
              : 'bg-blue-600 text-white shadow-sm shadow-blue-950/40 hover:bg-blue-500'
          }`}
        >
          <Play className={`w-3.5 h-3.5 ${isCalculating ? 'animate-pulse' : ''}`} />
          Calcular
        </button>
      )}

      {/* Botao Melhorar (dropdown) */}
      {canOptimize && (
        <div className="relative">
          <button
            onClick={() => setShowOptimizeMenu(!showOptimizeMenu)}
            disabled={isProcessing}
            className={`flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-medium transition-colors ${
              isProcessing
                ? 'border border-slate-400/10 bg-slate-500/5 text-slate-400/30 cursor-not-allowed'
                : 'border border-slate-400/20 bg-slate-400/10 text-slate-200 hover:bg-slate-400/15'
            }`}
            title="Otimizar resultado com mais tentativas"
          >
            <TrendingUp className={`w-3.5 h-3.5 ${isOptimizing ? 'animate-pulse' : ''}`} />
            Melhorar
            <ChevronDown className="w-3 h-3" />
          </button>

          {showOptimizeMenu && !isProcessing && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowOptimizeMenu(false)}
              />
              {/* Menu */}
              <div className="absolute bottom-full left-0 mb-1 bg-navy-800 border border-navy-600 rounded-lg shadow-xl z-20 min-w-[160px] overflow-hidden">
                {OPTIMIZE_LEVELS.map(({ key, label, attempts }) => (
                  <button
                    key={key}
                    onClick={() => {
                      setShowOptimizeMenu(false);
                      onOptimize(key);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-navy-700 text-gray-200 transition-colors"
                  >
                    <span className="font-medium">{label}</span>
                    <span className="text-gray-500">{attempts} tentativas</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Botao Aplicar no CorelDRAW */}
      {canApply && (
        <button
          onClick={onApply}
          disabled={isProcessing}
          className={`flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
            isProcessing
              ? 'border border-blue-400/10 bg-blue-500/5 text-blue-400/30 cursor-not-allowed'
              : 'border border-blue-400/25 bg-blue-400/10 text-blue-100 hover:bg-blue-400/15'
          }`}
          title="Aplicar posicoes nos shapes do CorelDRAW"
        >
          <CheckCircle className={`w-3.5 h-3.5 ${isApplying ? 'animate-pulse' : ''}`} />
          Aplicar
        </button>
      )}

      {/* Reset */}
      <button
        onClick={onReset}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-navy-700/70 hover:text-gray-300"
        aria-label="Resetar MontaTech"
        title="Resetar"
      >
        <RotateCcw className="w-4 h-4" />
      </button>
    </div>
  );
}
